import {
  AccountingPlatformBillLabelSelectWidget,
  AccountingPlatformCategorySelectWidget,
  VendorSelectWidget,
} from '@melio/ap-widgets';
import { Control, Fade, Form, NakedButton, useWatch } from '@melio/penny';
import { AccountingPlatform, AccountingPlatformBillLabel, AccountingPlatformSlug } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { isEmpty } from 'lodash';

import { useVendorSelectSideEffects } from '../../../../../../../utils/useVendorSelectSideEffects';
import { isSinglePaymentFields } from '../../../../../AddBillV2/utils';
import { AddBillV2FormFrequency, AddBillV2FormValues, RegisterFieldFn } from '../../../../types';
import { RecurringBillFormFields } from './RecurringBillFormFields';
import { StpAutoPaymentProcessingBanner } from './StpAutoPaymentProcessingBanner';

type Props = {
  hasInvoice?: boolean;
  formControl: Control<AddBillV2FormValues>;
  registerField: RegisterFieldFn;
  isMinifiedView?: boolean;
  allowRecurring?: boolean;
  shouldClearVendorIdField?: boolean;
  amountFieldHelper?: string | null;
  isFormHasLineItems?: boolean;
  activeAccountingPlatform?: AccountingPlatform;
  billLabel?: AccountingPlatformBillLabel;
  isConnectedToAccountingPlatform?: boolean;
  toggleView: () => void;
  onAmountChange: (value: string) => void;
};

export const BillDetailsForm = ({
  hasInvoice,
  formControl,
  registerField,
  isMinifiedView,
  allowRecurring,
  shouldClearVendorIdField,
  amountFieldHelper,
  isFormHasLineItems,
  activeAccountingPlatform,
  isConnectedToAccountingPlatform,
  billLabel,
  toggleView,
  onAmountChange,
}: Props) => {
  const { formatMessage } = useMelioIntl();
  const [frequency, amount, invoiceDate, dueDate, vendorId] = useWatch({
    control: formControl,
    name: ['frequency', 'amount', 'invoiceDate', 'dueDate', 'vendorId'],
  });
  const isRecurringFlow = !isSinglePaymentFields(frequency);
  const recurringFrequenciesOptions = Object.values(AddBillV2FormFrequency).map((frequency) => ({
    label: formatMessage(`activities.addBillV2.billForm.frequencyPicker.${frequency}`),
    value: frequency,
    testId: frequency,
  }));

  const isAmountReadOnly = isFormHasLineItems && frequency === AddBillV2FormFrequency.ONE_TIME;
  const isQuickBooksOnline = activeAccountingPlatform?.accountingSlug === AccountingPlatformSlug.QuickBooksOnline;
  const accountingPlatformCategoryLabel = isQuickBooksOnline
    ? formatMessage('activities.addInstantBillV2Form.category.label.quickbooks')
    : formatMessage('activities.addInstantBillV2Form.category.label.general');
  const analyticsPropertiesForVendorSelect = {
    Flow: 'vendor',
    PageName: 'bill-details',
    VendorCreatedOrigin: 'bill',
    Intent: 'add-a-vendor',
  };
  const { isSuvcOverStpVendor } = useVendorSelectSideEffects(vendorId || '');
  const getMinDueDate = (): Date | undefined => {
    if (invoiceDate && (dueDate === undefined || invoiceDate > dueDate)) {
      return invoiceDate;
    }
    return undefined;
  };

  const getAmountTooltip = () => {
    if (!isAmountReadOnly) {
      return;
    }
    return { label: formatMessage('activities.addBillV2.billForm.billAmount.tooltip') };
  };

  const shouldShowBillLabelSelect = !isEmpty(billLabel) && !isRecurringFlow;

  return (
    <>
      <VendorSelectWidget
        {...registerField('vendorId')}
        data-testid="add-bill-v2-vendors-picker"
        placeholder={formatMessage('activities.addBillV2.billForm.vendorPicker.placeholder')}
        colSpan={16}
        labelProps={{ label: formatMessage('activities.addBillV2.billForm.vendorPicker.label') }}
        shouldClearField={shouldClearVendorIdField}
        analyticsProperties={analyticsPropertiesForVendorSelect}
      />
      <Form.AmountField
        {...registerField('amount')}
        isReadOnly={isAmountReadOnly}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAmountChange(e.target.value)}
        data-testid="add-bill-v2-amount-field"
        colSpan={!allowRecurring ? 16 : 8}
        labelProps={{
          label: formatMessage('activities.addBillV2.billForm.billAmount.placeholder'),
          tooltipProps: getAmountTooltip(),
        }}
        helperTextProps={
          !hasInvoice && amount && parseFloat(amount) >= 10000
            ? {
                label: formatMessage('activities.addBillV2.billForm.billAmount.10khelperText'),
              }
            : amountFieldHelper
            ? {
                label: amountFieldHelper,
              }
            : undefined
        }
      />
      {allowRecurring ? (
        <Form.DeprecatedSelect
          {...registerField('frequency')}
          helperTextProps={{ label: formatMessage('activities.addBillV2.billForm.frequencyPicker.helperText') }}
          data-testid="add-bill-v2-frequency-picker"
          options={recurringFrequenciesOptions}
          labelProps={{ label: formatMessage('activities.addBillV2.billForm.frequencyPicker.placeholder') }}
          colSpan={8}
          // DO NOT MIGRATE TO Form.Select UNTIL Form.Select v3 is released
          isClearable={false} // replace with emptyState={undefined} once migrated to Form.Select
          shouldHideCaret
        />
      ) : null}

      {!!isRecurringFlow && <RecurringBillFormFields formControl={formControl} registerField={registerField} />}

      {!!isMinifiedView && !!isConnectedToAccountingPlatform && (
        <AccountingPlatformCategorySelectWidget
          {...registerField('categoryId')}
          data-testid="add-bill-v2-minified-category"
          colSpan={16}
          labelProps={{ label: accountingPlatformCategoryLabel }}
        />
      )}

      {!!isMinifiedView && (
        <Form.ContentBox colSpan={16}>
          <NakedButton
            data-testid="add-bill-v2-combine-button"
            variant="secondary"
            size="medium"
            label={formatMessage('activities.addBillV2.billForm.toggleView', {
              icon: isMinifiedView ? '+' : ' - ',
            })}
            onClick={toggleView}
          />
        </Form.ContentBox>
      )}

      {!isRecurringFlow && !isMinifiedView && (
        <>
          <Form.DateField
            {...registerField('invoiceDate')}
            toggleDatePickerAriaLabel={formatMessage(
              'activities.addBillV2.billForm.invoiceDate.toggleDatePickerAriaLabel'
            )}
            data-testid="add-bill-v2-invoice-date-field"
            weekDays={[0, 1, 2, 3, 4, 5, 6]}
            excludeHolidays={false}
            labelProps={{ label: formatMessage('activities.addBillV2.billForm.invoiceDate.placeholder') }}
            colSpan={8}
            placeholder={formatMessage('activities.addBillV2.billForm.invoiceDate.field.placeholder')}
          />
          <Form.DateField
            {...registerField('dueDate')}
            isRequired
            data-testid="add-bill-v2-due-date-field"
            labelProps={{ label: formatMessage('activities.addBillV2.billForm.dueDate.placeholder') }}
            toggleDatePickerAriaLabel={formatMessage('activities.addBillV2.billForm.dueDate.toggleDatePickerAriaLabel')}
            excludeHolidays={false}
            weekDays={[0, 1, 2, 3, 4, 5, 6]}
            colSpan={8}
            minDate={getMinDueDate()}
            placeholder={formatMessage('activities.addBillV2.billForm.dueDate.field.placeholder')}
          />
        </>
      )}

      {!isMinifiedView && (
        <>
          <Form.TextField
            {...registerField('invoiceNumber')}
            data-testid="add-bill-v2-invoice-number-field"
            labelProps={{ label: formatMessage('activities.addBillV2.billForm.invoiceNumber.placeholder') }}
            helperTextProps={{ label: formatMessage('activities.addBillV2.billForm.invoiceNumber.helperText') }}
            colSpan={8}
          />
          {shouldShowBillLabelSelect && (
            <AccountingPlatformBillLabelSelectWidget
              {...registerField('externalLabelId')}
              billLabel={billLabel}
              colSpan={8}
              data-testid="add-bill-v2-bill-label-picker"
            />
          )}
          <Form.TextField
            {...registerField('noteToSelf')}
            data-testid="add-bill-v2-note-to-self-field"
            placeholder={formatMessage('activities.addBillV2.billForm.noteToSelf.placeholder')}
            labelProps={{ label: formatMessage('activities.addBillV2.billForm.noteToSelf.label') }}
            colSpan={shouldShowBillLabelSelect ? 16 : 8}
          />
        </>
      )}
      <Form.ContentBox colSpan={16}>
        <Fade in={isSuvcOverStpVendor}>
          <StpAutoPaymentProcessingBanner />
        </Fade>
      </Form.ContentBox>
    </>
  );
};
